import {hasMatch} from 'fzy.js'
import {
  type FilterProvider,
  FilterItem,
  type SearchProvider,
  SearchItem,
  type QueryEvent,
  type QueryFilterElement,
  Octicon,
} from '@github-ui/query-builder-element/query-builder-api'
import type {QueryBuilderElement} from '@github-ui/query-builder-element'

interface Suggestion {
  value: string
  description?: string
}

const ID = 'dependencies-search'
const TARGET_ID = `query-builder-${ID}`
const relationships = ['direct', 'transitive', 'inconclusive']
const getSearchUrl = document.querySelector<HTMLFormElement>(`#${ID}-form`)?.getAttribute('action') || ''
function getSearchInput(): HTMLElement {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  return document.querySelector<HTMLElement>('query-builder#query-builder-dependencies-search')!
}
const filterSuggestions = (suggestions: string[], query: string) =>
  suggestions.filter(suggestion => {
    if ((query && hasMatch(query, suggestion)) || query === '') return suggestion
  })

async function fetchEcosystemSuggestions(): Promise<Suggestion[]> {
  return JSON.parse(getSearchInput()?.getAttribute('data-suggestable-ecosystems') as string)
}
const ecosystems = async (): Promise<Suggestion[]> => await fetchEcosystemSuggestions()

class SearchItemProvider extends EventTarget implements SearchProvider {
  priority = 1
  name = 'Search'
  singularItemName = 'search item'
  value = 'search'
  type = 'search' as const

  queryBuilder: QueryBuilderElement
  constructor(queryBuilder: QueryBuilderElement) {
    super()
    this.queryBuilder = queryBuilder
    this.queryBuilder.addEventListener('query', this)
    this.queryBuilder.attachProvider(this)
  }

  handleEvent(event: QueryEvent) {
    if (String(event) === '') return
    this.dispatchEvent(
      new SearchItem({
        priority: 1,
        value: event.toString(),
        icon: Octicon.Search,
        scope: 'GENERAL',
        action: {
          url: `${getSearchUrl}?q=${event.toString()}`,
        },
      }),
    )
  }
}

class RelationshipProvider extends EventTarget implements FilterProvider {
  name = 'Relationships'
  singularItemName = 'relationship'
  value = 'relationship'
  priority = 1
  type = 'filter' as const

  queryBuilder: QueryBuilderElement
  constructor(queryBuilder: QueryBuilderElement) {
    super()
    this.queryBuilder = queryBuilder
    this.queryBuilder.addEventListener('query', this)
    this.queryBuilder.attachProvider(this)
  }

  handleEvent(event: QueryEvent) {
    const lastQuery = event.parsedQuery.at(-1)
    const lastQueryValue = lastQuery?.value || ''
    const lastQueryType = lastQuery?.type
    const lastQueryFilter = (lastQuery as QueryFilterElement)?.filter || ''

    const filteredRelationships = filterSuggestions(relationships, lastQueryValue)

    if (lastQueryType !== 'filter' && (hasMatch(lastQueryValue, this.value) || lastQueryValue === '')) {
      this.dispatchEvent(new Event('show'))
    }

    if (lastQueryType !== 'filter' || lastQueryFilter !== this.value) return

    for (const relationship of filteredRelationships) {
      this.dispatchEvent(new FilterItem({filter: 'relationship', value: relationship}))
    }
  }
}

class EcosystemProvider extends EventTarget implements FilterProvider {
  name = 'Ecosystems'
  singularItemName = 'ecosystem'
  value = 'ecosystem'
  priority = 2
  type = 'filter' as const

  queryBuilder: QueryBuilderElement
  constructor(queryBuilder: QueryBuilderElement) {
    super()
    this.queryBuilder = queryBuilder
    this.queryBuilder.addEventListener('query', this)
    this.queryBuilder.attachProvider(this)
  }

  async handleEvent(event: QueryEvent) {
    const lastQuery = event.parsedQuery.at(-1)
    const lastQueryValue = lastQuery?.value || ''
    const lastQueryType = lastQuery?.type
    const lastQueryFilter = (lastQuery as QueryFilterElement)?.filter || ''

    const getEcosystems = await ecosystems()
    const ecosystemValues = getEcosystems.map(ecosystem => ecosystem.value)
    const filteredEcosystems = filterSuggestions(ecosystemValues, lastQueryValue)

    if (
      lastQueryType !== 'filter' &&
      (hasMatch(lastQueryValue, this.value) || lastQueryValue === '') &&
      filteredEcosystems.length > 0
    ) {
      this.dispatchEvent(new Event('show'))
    }

    if (lastQueryType !== 'filter' || lastQueryFilter !== this.value) return

    for (const ecosystem of filteredEcosystems) {
      this.dispatchEvent(new FilterItem({filter: 'ecosystem', value: ecosystem}))
    }
  }
}

document.addEventListener('query-builder:request-provider', (event: Event) => {
  const eventTarget: QueryBuilderElement | null = event.target as QueryBuilderElement
  if (!eventTarget || eventTarget.id !== TARGET_ID) return

  new SearchItemProvider(event.target as QueryBuilderElement)
  new RelationshipProvider(event.target as QueryBuilderElement)
  new EcosystemProvider(event.target as QueryBuilderElement)
})
